import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const headerRef = useRef(null);

  useEffect(() => {
    document.body.style.paddingTop = '80px';
    return () => {
      document.body.style.paddingTop = '';
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!headerRef.current) return;
      const rect = headerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    const header = headerRef.current;
    if (header) {
      header.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (header) {
        header.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setIsScrolled(currentScrollY > 50);
      
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY && currentScrollY - lastScrollY > 10) {
          setIsHeaderVisible(false);
        } else if (lastScrollY - currentScrollY > 10) {
          setIsHeaderVisible(true);
        }
      } else {
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
      
      const sections = ['home', 'journey', 'vumaqash', 'countries', 'testimonials', 'contact'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [lastScrollY]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'auto';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    closeMobileMenu();
    
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      setIsHeaderVisible(true);
      
      window.scrollTo({
        top: targetElement.offsetTop - (headerRef.current?.offsetHeight || 80),
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { id: 'home', label: 'Home', icon: '🏠', gradient: 'from-blue-500 to-cyan-500' },
    { id: 'journey', label: 'My Story', icon: '🚀', gradient: 'from-purple-500 to-pink-500' },
    { id: 'vumaqash', label: 'VumaQash', icon: '💎', gradient: 'from-amber-500 to-orange-500' },
    { id: 'countries', label: 'Earn', icon: '💰', gradient: 'from-green-500 to-emerald-500' },
    { id: 'testimonials', label: 'Success Stories', icon: '⭐', gradient: 'from-yellow-500 to-amber-500' },
    { id: 'contact', label: 'Contact', icon: '📞', gradient: 'from-indigo-500 to-purple-500' }
  ];

  return (
    <>
      <header 
        ref={headerRef}
        id="header" 
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        {/* Glass Morphism Background with Dynamic Spotlight */}
        <div 
          className={`absolute inset-0 transition-all duration-500 ${
            isScrolled 
              ? 'bg-gray-900/80 backdrop-blur-2xl shadow-2xl shadow-black/40' 
              : 'bg-gradient-to-br from-purple-900/90 via-blue-900/90 to-emerald-900/90 backdrop-blur-2xl'
          }`}
          style={{
            background: isScrolled 
              ? `radial-gradient(300px at ${mousePosition.x}px ${mousePosition.y}px, rgba(120, 119, 198, 0.3), transparent),
                 rgba(17, 24, 39, 0.8)`
              : `radial-gradient(400px at ${mousePosition.x}px ${mousePosition.y}px, rgba(168, 85, 247, 0.4), transparent),
                 radial-gradient(300px at ${mousePosition.x * 0.7}px ${mousePosition.y * 1.3}px, rgba(59, 130, 246, 0.3), transparent),
                 radial-gradient(200px at ${mousePosition.x * 1.2}px ${mousePosition.y * 0.8}px, rgba(16, 185, 129, 0.2), transparent)`
          }}
        />
        
        {/* Animated Border */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />

        <div className="container mx-auto px-4 lg:px-6 relative z-10">
          <div className="flex justify-between items-center">
            {/* Enhanced Logo */}
            <Link 
              to="/" 
              className="logo group flex items-center space-x-3"
              onClick={closeMobileMenu}
            >
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-yellow-500/30 group-hover:scale-110 transition-all duration-500 border-2 border-yellow-300/50">
                  <span className="text-black font-black text-lg">CN</span>
                </div>
                {/* Floating particles */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-purple-400 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse" style={{animationDelay: '1s'}}></div>
              </div>
              <div className="flex flex-col">
                <span className="text-yellow-400 font-black text-2xl leading-6 drop-shadow-lg">Cleophas</span>
                <span className="text-cyan-400 font-black text-2xl leading-6 drop-shadow-lg">Network</span>
              </div>
              
              {/* Shine effect */}
              <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400/20 via-cyan-400/20 to-purple-400/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
            </Link>
            
            {/* Enhanced Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => handleNavClick(e, `#${item.id}`)}
                  className={`relative px-6 py-3 rounded-2xl font-bold transition-all duration-500 group overflow-hidden ${
                    activeSection === item.id
                      ? `text-white bg-gradient-to-r ${item.gradient} shadow-2xl scale-105`
                      : 'text-gray-300 hover:text-white bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:scale-105'
                  }`}
                >
                  {/* Animated background */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                    activeSection === item.id ? 'opacity-100' : ''
                  }`}></div>
                  
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <span className="relative flex items-center space-x-3">
                    <span className="text-lg drop-shadow-lg">{item.icon}</span>
                    <span className="drop-shadow-lg">{item.label}</span>
                  </span>
                  
                  {/* Active indicator */}
                  {activeSection === item.id && (
                    <>
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-1 bg-white rounded-full blur-sm animate-pulse"></div>
                      <div className="absolute -inset-1 bg-white/20 rounded-2xl blur-sm animate-pulse"></div>
                    </>
                  )}
                  
                  {/* Floating dots */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-bounce"></div>
                  <div className="absolute -bottom-1 -left-1 w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </a>
              ))}
            </nav>

            {/* Enhanced CTA Button */}
            <div className="hidden lg:flex items-center space-x-4">
              <a 
                href="https://chat.whatsapp.com/KBmtWKO6vEC9XLGdDpUTmJ" 
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-black py-3 px-8 rounded-2xl transition-all duration-500 shadow-2xl shadow-green-500/40 hover:shadow-green-500/60 hover:scale-105 overflow-hidden"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <span className="relative flex items-center space-x-3">
                  <span className="text-xl">💬</span>
                  <span className="drop-shadow-lg">Join Group</span>
                </span>
                
                {/* Floating particles */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-ping"></div>
              </a>
            </div>

            {/* Enhanced Mobile Menu Button */}
            <button 
              className="lg:hidden relative w-14 h-14 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-2xl shadow-cyan-500/40 hover:shadow-cyan-500/60 hover:scale-105 transition-all duration-500 group overflow-hidden"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className={`w-6 h-6 relative transition-transform duration-500 ${isMobileMenuOpen ? 'rotate-90' : ''}`}>
                <span className={`absolute top-2 left-0 w-6 h-0.5 bg-white rounded-full transition-all duration-500 ${
                  isMobileMenuOpen ? 'rotate-45 top-3' : ''
                }`}></span>
                <span className={`absolute top-3 left-0 w-6 h-0.5 bg-white rounded-full transition-all duration-500 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}></span>
                <span className={`absolute top-4 left-0 w-6 h-0.5 bg-white rounded-full transition-all duration-500 ${
                  isMobileMenuOpen ? '-rotate-45 top-3' : ''
                }`}></span>
              </div>
              
              {/* Glow effect */}
              <div className="absolute -inset-2 bg-cyan-400 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 -z-10"></div>
            </button>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-700/30 backdrop-blur-sm">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 transition-all duration-500 shadow-lg shadow-blue-500/50 relative overflow-hidden"
            style={{
              width: `${((navItems.findIndex(item => item.id === activeSection) + 1) / navItems.length) * 100}%`
            }}
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 animate-shimmer"></div>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible'
        }`}
      >
        {/* Glass Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-2xl"
          onClick={closeMobileMenu}
        />
        
        {/* Enhanced Menu Content */}
        <div 
          className={`absolute top-0 right-0 w-80 h-full bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-3xl shadow-2xl shadow-black/60 transform transition-transform duration-500 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Enhanced Header */}
          <div className="p-6 border-b border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-900/50">
            <div className="flex items-center justify-between">
              <Link 
                to="/" 
                className="logo group flex items-center space-x-3"
                onClick={closeMobileMenu}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-2xl border-2 border-yellow-300/50">
                  <span className="text-black font-black text-xl">CN</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-yellow-400 font-black text-2xl leading-6">Cleophas</span>
                  <span className="text-cyan-400 font-black text-2xl leading-6">Network</span>
                </div>
              </Link>
              
              <button 
                className="w-12 h-12 flex items-center justify-center bg-red-500/20 hover:bg-red-500/30 rounded-2xl text-red-400 hover:text-red-300 transition-all duration-500 shadow-lg hover:scale-105"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <span className="text-2xl">✕</span>
              </button>
            </div>
          </div>

          {/* Enhanced Navigation Items */}
          <nav className="p-6 space-y-3">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleNavClick(e, `#${item.id}`)}
                className={`relative flex items-center space-x-4 p-5 rounded-2xl transition-all duration-500 group overflow-hidden ${
                  activeSection === item.id
                    ? `bg-gradient-to-r ${item.gradient} shadow-2xl scale-105`
                    : 'bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:scale-105'
                }`}
              >
                {/* Animated background */}
                <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  activeSection === item.id ? 'opacity-100' : ''
                }`}></div>
                
                <span className={`text-2xl transition-transform duration-500 group-hover:scale-110 relative z-10 ${
                  activeSection === item.id ? 'scale-110' : ''
                }`}>
                  {item.icon}
                </span>
                <span className={`font-black text-lg relative z-10 ${
                  activeSection === item.id ? 'text-white' : 'text-gray-300 group-hover:text-white'
                }`}>
                  {item.label}
                </span>
                
                {/* Active indicator */}
                {activeSection === item.id && (
                  <div className="ml-auto w-3 h-3 bg-white rounded-full animate-ping relative z-10"></div>
                )}
              </a>
            ))}
          </nav>

          {/* Enhanced CTA Section */}
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-700/50 bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-2xl">
            <a 
              href="https://chat.whatsapp.com/KBmtWKO6vEC9XLGdDpUTmJ" 
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-black py-4 px-6 rounded-2xl transition-all duration-500 shadow-2xl shadow-green-500/40 hover:shadow-green-500/60 hover:scale-105 block text-center overflow-hidden"
              onClick={closeMobileMenu}
            >
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <span className="relative flex items-center justify-center space-x-3">
                <span className="text-2xl">💬</span>
                <span className="text-lg drop-shadow-lg">Join WhatsApp Group</span>
              </span>
            </a>
            
            {/* Enhanced Social Proof */}
            <div className="mt-4 text-center">
              <p className="text-gray-400 text-sm">
                <span className="text-yellow-400 font-black">2,347+</span> Active Members
              </p>
              <div className="flex justify-center space-x-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className="text-yellow-400 text-sm">⭐</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Header;